import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { useState, useEffect, useMemo } from 'react';
import Breadcrumb from '~/components/Breadcrumbs/Breadcrumb';
import { IGrant } from '~/types/campaigns';
import {
    MdNavigateBefore,
    MdOutlineEdit,
    MdNavigateNext,
} from 'react-icons/md';
import { RiDeleteBinLine } from 'react-icons/ri';
import moment from 'moment';
import AsyncSelect from 'react-select/async';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import {
    deleteAppliedGrant,
    fetchAppliedGrants,
    searchGrantNames,
    searchGrantCampaigns,
    searchGrantNtafs,
    GrantNameOption,
    GrantCampaignOption,
    GrantNtafOption,
} from '~/services/grants';
import ApplyGrantDrawer from './ApplyGrantDrawer';

const columnHelper = createColumnHelper<IGrant & { action?: string }>();

const buildColumns = (
    onEdit: (grant: IGrant) => void,
    onDelete: (grant: IGrant) => void
) => [
    columnHelper.accessor('name', {
        header: () => 'Grant Name',
        cell: ({ row, renderValue }) => (
            <button
                type='button'
                onClick={() => onEdit(row.original)}
                className='text-left font-bold text-primary hover:underline'
            >
                {renderValue()}
            </button>
        ),
        meta: { className: 'text-left', width: '28%' },
    }),
    columnHelper.accessor('campaign', {
        header: () => 'Campaign',
        cell: ({ row, renderValue }) => {
            const value = renderValue();
            const campaignId = row.original.campaign_id;
            if (campaignId) {
                return (
                    <Link
                        to={`/campaigns/${campaignId}`}
                        className='font-bold text-primary hover:underline'
                    >
                        {value}
                    </Link>
                );
            }
            return value;
        },
        meta: { width: '28%' },
    }),
    columnHelper.accessor('ntaf_id', {
        header: () => 'NTAF ID',
        cell: (props) => props.renderValue(),
    }),
    columnHelper.accessor('amount', {
        header: () => 'Amount',
        cell: (props) => {
            const value = props.getValue();
            const num = typeof value === 'number' ? value : parseFloat(value);
            if (Number.isNaN(num)) return value;
            return num.toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
            });
        },
    }),
    columnHelper.accessor('date', {
        header: () => 'Date',
        cell: (props) => moment(props.getValue()).format('MM/DD/YYYY'),
    }),
    columnHelper.accessor('action', {
        header: () => '',
        cell: ({ row }) => (
            <div className='flex justify-end gap-3'>
                <button
                    type='button'
                    onClick={() => onEdit(row.original)}
                    aria-label='Edit grant'
                    className='text-primary hover:opacity-70'
                >
                    <MdOutlineEdit size={20} />
                </button>
                <button
                    type='button'
                    onClick={() => onDelete(row.original)}
                    aria-label='Delete grant'
                    className='text-red hover:opacity-70'
                >
                    <RiDeleteBinLine size={20} />
                </button>
            </div>
        ),
    }),
];

enum Sort {
    ASC = 'asc',
    DESC = 'desc',
}

type DateRange = {
    start_date: string;
    end_date: string;
};

const initialDateRange = {
    start_date: '',
    end_date: '',
};

const asyncSelectStyles = {
    menuPortal: (base: Record<string, unknown>) => ({
        ...base,
        zIndex: 9999,
    }),
};

const AppliedGrants: React.FC = () => {
    const [sort, setSort] = useState<Sort | null>(null);
    const [sortBy, setSortBy] = useState<string | null>(null);
    const [dateRange, setDateRange] = useState<DateRange>(initialDateRange);
    const [selectedGrant, setSelectedGrant] = useState<GrantNameOption | null>(
        null
    );
    const [selectedCampaign, setSelectedCampaign] =
        useState<GrantCampaignOption | null>(null);
    const [selectedNtaf, setSelectedNtaf] = useState<GrantNtafOption | null>(
        null
    );
    const [data, setData] = useState<IGrant[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [editingGrant, setEditingGrant] = useState<IGrant | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [grantToDelete, setGrantToDelete] = useState<IGrant | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const columns = useMemo(
        () =>
            buildColumns(
                (grant) => {
                    setEditingGrant(grant);
                    setIsDrawerOpen(true);
                },
                (grant) => setGrantToDelete(grant)
            ),
        []
    );

    const confirmDelete = async () => {
        if (!grantToDelete) return;
        setIsDeleting(true);
        try {
            await deleteAppliedGrant(grantToDelete.id);
            toast.success('Grant deleted.');
            setGrantToDelete(null);
            setRefreshKey((k) => k + 1);
        } catch (err) {
            const message =
                err instanceof Error ? err.message : 'Failed to delete grant.';
            console.error('Failed to delete grant:', err);
            toast.error(message);
        } finally {
            setIsDeleting(false);
        }
    };

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        const ntafFilter =
            selectedNtaf?.value || selectedCampaign?.value || undefined;
        fetchAppliedGrants({
            name: selectedGrant?.value || undefined,
            ntaf_id: ntafFilter,
            start_date: dateRange.start_date || undefined,
            end_date: dateRange.end_date || undefined,
        })
            .then((grants) => {
                if (!cancelled) setData(grants);
            })
            .catch((err) => {
                console.error('Failed to fetch applied grants:', err);
                if (!cancelled) setData([]);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [
        selectedGrant?.value,
        selectedCampaign?.value,
        selectedNtaf?.value,
        dateRange.start_date,
        dateRange.end_date,
        refreshKey,
    ]);

    const loadGrantOptions = async (
        inputValue: string
    ): Promise<GrantNameOption[]> => {
        try {
            return await searchGrantNames(inputValue);
        } catch (err) {
            console.error('Error searching grant names:', err);
            return [];
        }
    };

    const loadCampaignOptions = async (
        inputValue: string
    ): Promise<GrantCampaignOption[]> => {
        try {
            return await searchGrantCampaigns(inputValue);
        } catch (err) {
            console.error('Error searching grant campaigns:', err);
            return [];
        }
    };

    const loadNtafOptions = async (
        inputValue: string
    ): Promise<GrantNtafOption[]> => {
        try {
            return await searchGrantNtafs(inputValue);
        } catch (err) {
            console.error('Error searching grant NTAFs:', err);
            return [];
        }
    };

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    const handleSort = (columnId: string) => {
        if (sortBy === columnId) {
            setSort(sort === Sort.ASC ? Sort.DESC : Sort.ASC);
        } else {
            setSortBy(columnId);
            setSort(Sort.ASC);
        }
    };

    return (
        <>
            <Breadcrumb pageName='Applied Grants' />

            <section className='data-table-common rounded-sm border border-stroke bg-white py-4 shadow-default dark:border-strokedark dark:bg-boxdark'>
                <div className='flex items-center justify-between gap-4 px-8 pb-4'>
                    <div className='flex flex-wrap items-center gap-3'>
                        <div className='w-[220px]'>
                            <AsyncSelect<GrantNameOption>
                                inputId='grant'
                                value={selectedGrant}
                                onChange={(option) => setSelectedGrant(option)}
                                loadOptions={loadGrantOptions}
                                defaultOptions
                                placeholder='Grant'
                                isClearable
                                isSearchable
                                cacheOptions
                                menuPortalTarget={document.body}
                                menuPosition='fixed'
                                styles={asyncSelectStyles}
                                noOptionsMessage={({ inputValue }) =>
                                    inputValue
                                        ? `No grants matching "${inputValue}"`
                                        : 'Type to search grants'
                                }
                            />
                        </div>
                        <div className='w-[220px]'>
                            <AsyncSelect<GrantCampaignOption>
                                inputId='campaign'
                                value={selectedCampaign}
                                onChange={(option) =>
                                    setSelectedCampaign(option)
                                }
                                loadOptions={loadCampaignOptions}
                                defaultOptions
                                placeholder='Campaign'
                                isClearable
                                isSearchable
                                cacheOptions
                                menuPortalTarget={document.body}
                                menuPosition='fixed'
                                styles={asyncSelectStyles}
                                noOptionsMessage={({ inputValue }) =>
                                    inputValue
                                        ? `No campaigns matching "${inputValue}"`
                                        : 'Type to search campaigns'
                                }
                            />
                        </div>
                        <div className='w-[160px]'>
                            <AsyncSelect<GrantNtafOption>
                                inputId='ntaf'
                                value={selectedNtaf}
                                onChange={(option) => setSelectedNtaf(option)}
                                loadOptions={loadNtafOptions}
                                defaultOptions
                                placeholder='NTAF ID'
                                isClearable
                                isSearchable
                                cacheOptions
                                menuPortalTarget={document.body}
                                menuPosition='fixed'
                                styles={asyncSelectStyles}
                                noOptionsMessage={({ inputValue }) =>
                                    inputValue
                                        ? `No NTAFs matching "${inputValue}"`
                                        : 'Type to search NTAFs'
                                }
                            />
                        </div>
                        <input
                            type='date'
                            name='start_date'
                            className='rounded border border-stroke p-[6px] px-3 py-2 outline-none focus:border-primary'
                            value={dateRange.start_date}
                            onChange={(event) =>
                                setDateRange({
                                    ...dateRange,
                                    start_date: event.target.value,
                                })
                            }
                        />
                        to
                        <input
                            type='date'
                            name='end_date'
                            className='rounded border border-stroke p-[6px] px-3 py-2 outline-none focus:border-primary'
                            value={dateRange.end_date}
                            onChange={(event) =>
                                setDateRange({
                                    ...dateRange,
                                    end_date: event.target.value,
                                })
                            }
                        />
                    </div>
                    <button
                        type='button'
                        onClick={() => {
                            setEditingGrant(null);
                            setIsDrawerOpen(true);
                        }}
                        className='inline-flex shrink-0 items-center justify-center gap-2.5 rounded-full border-[2px] border-warning bg-warning px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-black transition-colors duration-500 hover:border-[#e6f7f8] hover:bg-[#e6f7f8]'
                    >
                        Apply Grant
                    </button>
                </div>

                <div
                    className='flex items-center justify-between border-t border-stroke px-8 py-1'
                    style={{ fontSize: '12px' }}
                >
                    <p>Showing {data.length} results</p>

                    <div className='flex items-center gap-5'>
                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <MdOutlineEdit className='text-primary' size={18} />
                            Edit
                        </div>
                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <RiDeleteBinLine className='text-red' size={18} />
                            Delete
                        </div>
                    </div>
                </div>

                <div className='overflow-x-auto'>
                    <table className='datatable-table datatable-one w-full table-auto border-collapse break-words px-4 md:px-8 2xl:table-fixed'>
                        <thead className='border-separate px-4'>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr
                                    className='border-t border-stroke bg-whiten'
                                    style={{ fontSize: '14px' }}
                                    key={headerGroup.id}
                                >
                                    {headerGroup.headers.map((header) => {
                                        const meta = header.column.columnDef
                                            .meta as
                                            | {
                                                  className?: string;
                                                  width?: string;
                                              }
                                            | undefined;
                                        return (
                                            <th
                                                style={{
                                                    paddingTop: '16px',
                                                    paddingBottom: '16px',
                                                    width: meta?.width,
                                                }}
                                                className={meta?.className}
                                                key={header.id}
                                            >
                                                <div className='flex items-center'>
                                                    <span
                                                        onClick={() =>
                                                            handleSort(
                                                                header.column.id
                                                            )
                                                        }
                                                        className='cursor-pointer'
                                                    >
                                                        {header.isPlaceholder
                                                            ? null
                                                            : flexRender(
                                                                  header.column
                                                                      .columnDef
                                                                      .header,
                                                                  header.getContext()
                                                              )}
                                                    </span>
                                                </div>
                                            </th>
                                        );
                                    })}
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {isLoading && data.length === 0 ? (
                                <tr className='border-t border-stroke'>
                                    <td
                                        colSpan={columns.length}
                                        className='py-6 text-center text-secondary'
                                    >
                                        Loading...
                                    </td>
                                </tr>
                            ) : data.length === 0 ? (
                                <tr className='border-t border-stroke'>
                                    <td
                                        colSpan={columns.length}
                                        className='py-6 text-center text-secondary'
                                    >
                                        No grants found.
                                    </td>
                                </tr>
                            ) : (
                                table.getRowModel().rows.map((row) => {
                                    return (
                                        <tr
                                            className='border-t border-stroke'
                                            key={row.id}
                                        >
                                            {row
                                                .getVisibleCells()
                                                .map((cell) => {
                                                    const meta = cell.column
                                                        .columnDef.meta as
                                                        | {
                                                              className?: string;
                                                          }
                                                        | undefined;
                                                    return (
                                                        <td
                                                            style={{
                                                                paddingTop:
                                                                    '16px',
                                                                paddingBottom:
                                                                    '16px',
                                                            }}
                                                            className={
                                                                meta?.className
                                                            }
                                                            key={cell.id}
                                                        >
                                                            {flexRender(
                                                                cell.column
                                                                    .columnDef
                                                                    .cell,
                                                                cell.getContext()
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                <div className='flex justify-between border-t border-stroke px-8 pt-5'>
                    <p className='font-light'>Showing {data.length} results</p>
                    <div className='flex'>
                        <button className='flex cursor-pointer items-center justify-center rounded-md p-1 px-2 hover:bg-primary'>
                            <MdNavigateBefore size={20} />
                        </button>

                        <button className='mx-1 flex cursor-pointer items-center justify-center rounded-md bg-primary p-1 px-3 hover:bg-primary'>
                            1
                        </button>
                        <button className='flex cursor-pointer items-center justify-center rounded-md p-1 px-2 hover:bg-primary'>
                            <MdNavigateNext size={20} />
                        </button>
                    </div>
                </div>
            </section>

            <ApplyGrantDrawer
                isOpen={isDrawerOpen}
                setIsOpen={(open) => {
                    setIsDrawerOpen(open);
                    if (!open) setEditingGrant(null);
                }}
                grant={editingGrant}
                onSuccess={() => setRefreshKey((k) => k + 1)}
            />

            {grantToDelete && (
                <div
                    className='fixed left-0 top-0 z-999999 flex h-full min-h-screen w-full items-center justify-center bg-black/60 px-4 py-5'
                    onClick={() =>
                        !isDeleting && setGrantToDelete(null)
                    }
                >
                    <div
                        className='w-full max-w-md rounded-lg bg-white px-8 py-8'
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className='pb-2 text-left text-xl font-bold text-black'>
                            Delete Grant
                        </h3>
                        <p className='text-left'>
                            Are you sure you want to delete{' '}
                            <span className='font-bold'>
                                {grantToDelete.name}
                            </span>
                            ? This action cannot be undone.
                        </p>
                        <div className='mt-8 flex justify-end gap-3'>
                            <button
                                type='button'
                                onClick={() => setGrantToDelete(null)}
                                disabled={isDeleting}
                                className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-[#E6F7F8] hover:bg-[#E6F7F8]'
                            >
                                Cancel
                            </button>
                            <button
                                type='button'
                                onClick={confirmDelete}
                                disabled={isDeleting}
                                className='rounded-full border-[2px] border-red bg-red px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-white transition-colors duration-500 hover:opacity-90'
                            >
                                {isDeleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AppliedGrants;
